import { useState } from "react";
import cx from "classnames";

import { toggleValue } from "common/utils/sets";
import { isHit } from "common/utils/strings";

import CloseButton from "client/common/components/buttons/CloseButton";
import TextButton from "client/common/components/buttons/TextButton";
import Checkbox from "client/common/components/inputs/Checkbox";

import {
  PANEL_COLLAPSED_OPTION_LIMIT,
  PANEL_OPTION_SEARCH_THRESHOLD,
} from "../constants/filterDefinitions";
import { FilterValue, SearchFilterOption } from "../types";
import { collapsedOptions, nestByParentCategory } from "../utils";

import styles from "../styles/FilterPanel.module.less";

type FilterOptionListProps = {
  placeholder: string;
  options: SearchFilterOption<FilterValue>[];
  selected: FilterValue[];
  onChange: (next: FilterValue[]) => void;
  /** Disables options with no results; only correct where each selection refreshes the counts. */
  disablesEmptyOptions?: boolean;
};

/** The span is load-bearing: Checkbox's label is a flex container and would drop the whitespace. */
const highlightOptionMatch = ({ label, query }: { label: string; query: string }) => {
  const matchStart = query ? label.toLocaleLowerCase().indexOf(query.toLocaleLowerCase()) : -1;
  if (matchStart < 0) {
    return label;
  }
  return (
    <span>
      {label.slice(0, matchStart)}
      <mark className={styles.highlight}>{label.slice(matchStart, matchStart + query.length)}</mark>
      {label.slice(matchStart + query.length)}
    </span>
  );
};

const FilterOptionList = ({
  placeholder,
  options,
  selected,
  onChange,
  disablesEmptyOptions = false,
}: FilterOptionListProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [query, setQuery] = useState("");
  const selectedValues = new Set(selected);

  const isSearchable = options.length > PANEL_OPTION_SEARCH_THRESHOLD;
  const matchingOptions = query ? options.filter((option) => isHit(query, option.label)) : options;
  // A search already narrows the list; collapsing it too would hide the match behind a click.
  const visibleOptions =
    isExpanded || query
      ? matchingOptions
      : collapsedOptions({
          options: matchingOptions,
          selected,
          limit: PANEL_COLLAPSED_OPTION_LIMIT,
        });

  return (
    <div className={styles.group}>
      {isSearchable && (
        <div className={styles.optionSearchRow}>
          <input
            type="text"
            className={styles.optionSearch}
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
            placeholder={placeholder}
            aria-label={placeholder}
          />
          {!!query && (
            <CloseButton
              type="button"
              className={styles.optionSearchClear}
              aria-label="Clear search"
              onClick={() => setQuery("")}
            />
          )}
        </div>
      )}
      {!visibleOptions.length && <div className={styles.noMatches}>No matches</div>}
      {nestByParentCategory(visibleOptions).map(({ option, depth }) => {
        const isSelected = selectedValues.has(option.value);
        const isUnavailable = disablesEmptyOptions && option.count === 0 && !isSelected;
        return (
          <div
            key={String(option.value)}
            className={cx(
              styles.option,
              depth > 0 && styles.nestedOption,
              isUnavailable && styles.unavailableOption,
            )}
          >
            <Checkbox
              value={isSelected}
              disabled={isUnavailable}
              onChange={() => onChange([...toggleValue(selected, option.value)])}
              labelContent={
                <>
                  {highlightOptionMatch({ label: option.label, query })}
                  {!!option.detail && <span className={styles.detail}>{option.detail}</span>}
                </>
              }
              styles={{ container: styles.checkbox, label: styles.optionLabel }}
            />
            {option.count !== undefined && <span className={styles.count}>{option.count}</span>}
          </div>
        );
      })}
      {!query && visibleOptions.length < options.length && (
        <TextButton type="button" className={styles.showAll} onClick={() => setIsExpanded(true)}>
          Show all {options.length}
        </TextButton>
      )}
      {isExpanded && (
        <TextButton type="button" className={styles.showAll} onClick={() => setIsExpanded(false)}>
          Show less
        </TextButton>
      )}
    </div>
  );
};

export default FilterOptionList;
